import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import API from '../services/api';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { Field, StatusBadge, Notice, inputStyle, tabStyle, apiErrors } from '../components/insurance/formBits';
import { enrollmentBadge, payerSupportSummary } from '../insurance/format';

// Insurance Tools (admin): the everyday front door to the insurance module.
// Phase 1 ships Payer Lookup and Enrollments; eligibility checks and claim
// status arrive with the later phases and will appear here as more tabs.

export default function AdminInsuranceToolsPage() {
  const [tab, setTab] = useState('lookup');
  const [status, setStatus] = useState(null);
  const [payers, setPayers] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loadError, setLoadError] = useState('');

  const load = useCallback(async () => {
    try {
      const [st, p, e] = await Promise.all([API.get('/billing/status'), API.get('/billing/payers'), API.get('/billing/enrollments')]);
      setStatus(st.data); setPayers(p.data); setEnrollments(e.data); setLoadError('');
    } catch (err) { setLoadError(apiErrors(err).message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 16px' }}>
      <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--primary-color)' }}>Insurance Tools</h1>
      <p style={{ margin: '4px 0 12px', fontSize: 13, color: 'var(--text-muted)' }}>
        Look up payers, see who is enrolled where. Eligibility checks and claim status join this page in the next phases.
        {' '}<Link to="/admin/payers">Manage payers</Link> · <Link to="/admin/billing-settings">Billing settings</Link>
      </p>
      {status && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          <StatusBadge tone={status.stedi.configured ? 'good' : 'bad'}>Stedi: {status.stedi.configured ? `connected (${status.stedi.env} mode)` : 'not configured'}</StatusBadge>
          <StatusBadge tone={status.encryption.configured ? 'good' : 'bad'}>Encryption: {status.encryption.configured ? 'configured' : 'missing key'}</StatusBadge>
        </div>
      )}
      {loadError && <Notice tone="bad">{loadError}</Notice>}
      <div style={{ marginBottom: 12, borderBottom: '1px solid var(--border-color)' }}>
        <button style={tabStyle(tab === 'lookup')} onClick={() => setTab('lookup')}>Payer Lookup</button>
        <button style={tabStyle(tab === 'enrollments')} onClick={() => setTab('enrollments')}>Enrollments</button>
      </div>
      {tab === 'lookup' && <PayerLookupTab payers={payers} onAdded={load} stediConfigured={Boolean(status && status.stedi.configured)} />}
      {tab === 'enrollments' && <EnrollmentsTab enrollments={enrollments} />}
    </div>
  );
}

function PayerLookupTab({ payers, onAdded, stediConfigured }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const known = new Map(payers.map((p) => [p.stedi_payer_id, p]));

  async function search(e) {
    e.preventDefault(); setBusy(true); setMsg(null); setResults(null);
    try { setResults((await API.get('/billing/payers/search', { params: { q } })).data); }
    catch (err) { setMsg({ tone: 'bad', text: apiErrors(err).message }); }
    finally { setBusy(false); }
  }
  async function add(r) {
    setMsg(null);
    try {
      await API.post('/billing/payers', {
        displayName: r.displayName, stediPayerId: r.stediId, payerId: r.primaryPayerId,
        supportsEligibility: r.supports.eligibility, supportsClaims: r.supports.claims, supportsClaimStatus: r.supports.claimStatus, supportsEra: r.supports.era,
        notes: r.eraEnrollmentRequired && r.eraEnrollment ? `ERA needs enrollment (${String(r.eraEnrollment.type).toLowerCase().replace('_', '-')}, ${String(r.eraEnrollment.timeframe).toLowerCase()}).` : '',
      });
      setMsg({ tone: 'good', text: `${r.displayName} added. Set telehealth rules and enrollments on the Payers page.` });
      onAdded();
    } catch (err) { setMsg({ tone: 'bad', text: apiErrors(err).message }); }
  }

  return (
    <Card>
      {!stediConfigured && <Notice tone="warn">Stedi is not configured on this server, so live lookup is unavailable.</Notice>}
      <form onSubmit={search} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <Field label="Payer name or ID" hint="Searches Stedi's live payer network. No client data is sent." style={{ maxWidth: 460 }}>
          <input style={inputStyle} value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. Carelon, Anthem Kentucky, BHOVO" aria-label="Payer search" />
        </Field>
        <Button type="submit" disabled={busy || q.trim().length < 2 || !stediConfigured} style={{ marginBottom: 2 }}>{busy ? 'Searching…' : 'Search'}</Button>
      </form>
      {msg && <Notice tone={msg.tone}>{msg.text}</Notice>}
      {results && (
        <div style={{ overflowX: 'auto', marginTop: 10 }}>
          <table style={table}>
            <thead><tr style={{ background: 'var(--secondary-color)' }}>{['Payer', 'Payer ID', 'Stedi ID', 'Supports', ''].map((h, i) => <th key={i} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {results.length === 0 && <tr><td style={td} colSpan={5}>No matches.</td></tr>}
              {results.map((r) => {
                const existing = known.get(r.stediId);
                return (
                  <tr key={r.stediId} style={{ borderBottom: '1px solid #F0EDE8' }}>
                    <td style={td}><strong>{r.displayName}</strong>{r.aliases && r.aliases.length > 1 && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>also {r.aliases.filter((a) => a !== r.primaryPayerId).slice(0, 4).join(', ')}</div>}</td>
                    <td style={{ ...td, fontFamily: 'monospace' }}>{r.primaryPayerId}</td>
                    <td style={{ ...td, fontFamily: 'monospace' }}>{r.stediId}</td>
                    <td style={td}><div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>{payerSupportSummary(r).map((s) => <StatusBadge key={s} tone={s.startsWith('No ') ? 'bad' : 'good'}>{s}</StatusBadge>)}</div></td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {existing ? <StatusBadge tone="neutral">Already set up</StatusBadge> : <Button variant="subtle" style={smallBtn} onClick={() => add(r)}>Add as payer</Button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function EnrollmentsTab({ enrollments }) {
  const byTherapist = new Map();
  for (const e of enrollments) {
    if (!byTherapist.has(e.therapist_id)) byTherapist.set(e.therapist_id, { name: e.therapist_name, rows: [] });
    byTherapist.get(e.therapist_id).rows.push(e);
  }
  const groups = [...byTherapist.values()].sort((a, b) => a.name.localeCompare(b.name));
  return (
    <Card>
      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>
        Who can bill which payer, and since when. Edit on the <Link to="/admin/payers">Payers</Link> page.
      </div>
      {groups.length === 0 && <div style={{ color: 'var(--text-muted)' }}>No enrollments yet.</div>}
      {groups.map((g) => (
        <div key={g.name} style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 700, color: 'var(--primary-color)', marginBottom: 4 }}>{g.name}</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {g.rows.map((e) => {
              const b = enrollmentBadge({ status: e.status, effectiveDate: e.effective_date, terminationDate: e.termination_date });
              return <StatusBadge key={e.id} tone={b.tone} wrap>{e.payer_name}: {b.label}{e.era_enrolled ? ' · ERA' : ''}</StatusBadge>;
            })}
          </div>
        </div>
      ))}
    </Card>
  );
}

const table = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const th = { padding: '8px 10px', textAlign: 'left', color: 'var(--primary-color)', fontWeight: 600, whiteSpace: 'nowrap' };
const td = { padding: '8px 10px', verticalAlign: 'top' };
const smallBtn = { padding: '6px 10px', fontSize: 12 };
